import { useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  FinancialsService,
  FinancialStatementFullRead,
  FinancialStatementSmall,
  FinancialStatementVersionValidator,
} from 'api';
import { globalAction } from 'common/actions';
import { NO_FINANCIAL_STATEMENT } from 'common/constants/financials';
import { ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY } from 'common/constants/services/companies';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import FinancialStatementService from 'services/financialStatement';
import { extractServerErrorMessage, Logger } from 'utilities';
import useResponse from './useResponse';

export const useCreateFinancialStatement = () => {
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const create = useCallback(
    async data => {
      if (data) {
        const service = new FinancialStatementService();
        dispatch(globalAction.showLoadingProgress(true));
        let response = null;
        try {
          response = await service.create(data);
          setAction(false);
          successNotification('The new financial statement was created successfully');
        } catch (error) {
          const defaultErrorMessage = 'An error ocurred while creating the new financial statement';
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'create the new financial statement',
          });
        } finally {
          // Invalidate the Allocation Values query to refetch the data with the new values
          queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

          dispatch(globalAction.showLoadingProgress(false));
        }

        return response;
      }

      return null;
    },
    [dispatch, processErrorResponse, queryClient, setAction, successNotification]
  );

  return [create];
};

export const useCreateFinancialStatementVersion = () => {
  const [createdVersion, setCreatedVersion] = useState<FinancialStatementFullRead | undefined>();

  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const createVersion = useCallback(
    async (sourceFinancialStatementId: number, data: FinancialStatementVersionValidator) => {
      if (data) {
        dispatch(globalAction.showLoadingProgress(true));

        try {
          const newVersion = await FinancialsService.apiFinancialsFinancialPageCreateVersionCreate(
            sourceFinancialStatementId,
            data
          );
          setCreatedVersion(newVersion);
          setAction(false);
          successNotification('The financial statement version was created successfully');
        } catch (error: any) {
          const defaultErrorMessage = 'An error ocurred while creating the new financial statement version';
          processErrorResponse({ error, defaultErrorMessage });
        } finally {
          // Invalidate the Allocation Values query to refetch the data with the new values
          queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [dispatch, processErrorResponse, queryClient, setAction, successNotification]
  );

  return [createdVersion, createVersion];
};

export const useUpdateFinancialStatement = () => {
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const update = useCallback(
    async (id, data) => {
      if (data) {
        const service = new FinancialStatementService();
        dispatch(globalAction.showLoadingProgress(true));
        let response = null;
        try {
          response = await service.update(id, data);
          setAction(false);
          successNotification('The financial statement was updated successfully');
        } catch (error: any) {
          const serverErrorMessage = extractServerErrorMessage(error.response.body);
          const defaultErrorMessage =
            error.response?.body?.detail ??
            serverErrorMessage ??
            'An error ocurred while updating the new financial statement';
          
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'update the financial statement',
          });
        } finally {
          // Invalidate the Allocation Values query to refetch the data with the new values
          queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });
          dispatch(globalAction.showLoadingProgress(false));
        }
        return response;
      }
    },
    [dispatch, processErrorResponse, queryClient, setAction, successNotification]
  );

  return [update];
};

export const useDeleteFinancialStatement = () => {
  const service = new FinancialStatementService();
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const { processErrorResponse } = useResponse();

  const deleteFinancialStatement = async (id: string) => {
    if (id) {
      dispatch(globalAction.showLoadingProgress(true));

      try {
        await service.delete(id);
      } catch (error: any) {
        const defaultErrorMessage = 'An error ocurred while deleting the financial statement';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'delete the financial statement',
        });
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };

  return [deleteFinancialStatement];
};

export const useRetrieveFinancialStatement = () => {
  const [, dispatch] = useStore() as unknown as UseStoreValues;

  const { processErrorResponse } = useResponse();

  const retrieve = useCallback(
    async id => {
      const service = new FinancialStatementService();
      if (id) {
        dispatch(globalAction.showLoadingProgress(true));
        let response = null;
        try {
          response = await service.retrieve(id);
        } catch (error) {
          const defaultErrorMessage = 'An error ocurred while retrieving the financial statement';
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'get the financial statement',
          });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
        return response;
      }
    },
    [dispatch, processErrorResponse]
  );

  return [retrieve];
};

export const useListFinancialStatement = () => {
  const service = new FinancialStatementService();
  const [, dispatch] = useStore() as unknown as UseStoreValues;

  const list = async () => {
    dispatch(globalAction.showLoadingProgress(true));
    let response = null;
    try {
      response = await service.list();
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
    return response;
  };
  return [list];
};

export const useListByMeasurementPeriod = () => {
  const [, dispatch] = useStore() as unknown as UseStoreValues;

  const listByMeasurementPeriod = useCallback(
    async (measurementPeriodId: number) => {
      dispatch(globalAction.showLoadingProgress(true));
      let response: FinancialStatementSmall[] | null = null;
      try {
        response = await FinancialsService.apiFinancialsFinancialPageListByMeasurementPeriodList(
          measurementPeriodId.toString()
        );
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
      return response;
    },
    [dispatch]
  );

  return [listByMeasurementPeriod];
};

export const useGetByMeasurementDateId = () => {
  const service = new FinancialStatementService();
  const [, dispatch] = useStore() as unknown as UseStoreValues;

  const getByMeasurementDateId = async (measurementPeriodId: string) => {
    dispatch(globalAction.showLoadingProgress(true));
    let response = null;
    try {
      response = await service.getByMeasurementDateId(measurementPeriodId);
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
    return response;
  };

  return [getByMeasurementDateId];
};

export const useFinancialPeriodsFiscalYear = () => {
  const service = useMemo(() => new FinancialStatementService(), []);
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const getFinancialPeriodsFiscalYear = useCallback(
    async (companyMeasurementDateId, versionId) => {
      if (companyMeasurementDateId) {
        dispatch(globalAction.showLoadingProgress(true));
        let response = null;
        try {
          response = await service.getFinancialPeriodsFiscalYear(companyMeasurementDateId, versionId);
        } catch (error) {
          Logger.error('useGetFinancialPeriodsFiscalYear', error);
          const defaultWarningMessage = NO_FINANCIAL_STATEMENT;
          enqueueSnackbar(defaultWarningMessage, { variant: 'warning' });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
        return response;
      }
    },
    [dispatch, service, enqueueSnackbar]
  );

  return [getFinancialPeriodsFiscalYear];
};
